import { isDeepEqual } from 'remeda';

import { ISelectValue } from '@/shared/types/select';

import {
  DEFAULT_GIRTH_FILTER_RANGE,
  FILTERABLE_GIRTH_KEYS
} from '@/shared/constants';

import { clampRangeToBounds } from './filters';

/** Диапазон роста по умолчанию (см), используется для сравнения "не заполнено" */
export const DEFAULT_HEIGHT_RANGE = [0, 250];

/** Диапазон веса по умолчанию (кг), используется для сравнения "не заполнено" */
export const DEFAULT_WEIGHT_RANGE = [0, 200];

/** Диапазон возраста по умолчанию (лет), используется для сравнения "не заполнено" */
export const DEFAULT_AGE_RANGE = [0, 100];

/**
 * Дефолтные диапазоны по ключам фильтров — единый источник для «не заполнено»,
 * очистки, пресетов и query-параметров: рост, вес, три обхвата и возраст
 */
export const DEFAULT_RANGES: Record<string, number[]> = {
  height: DEFAULT_HEIGHT_RANGE,
  weight: DEFAULT_WEIGHT_RANGE,
  ...Object.fromEntries(
    FILTERABLE_GIRTH_KEYS.map(key => [key, DEFAULT_GIRTH_FILTER_RANGE])
  ),
  age: DEFAULT_AGE_RANGE
};

/** Ключи диапазонных фильтров — сравниваются с дефолтом при записи в URL */
export const RANGE_FILTER_KEYS: string[] = Object.keys(DEFAULT_RANGES);

/** Названия полей фильтров портфолио, значения которых являются массивами */
export const PORTFOLIO_ARRAY_FIELDS = [
  'hair_color',
  'hair_length',
  'eye_color',
  'appearance_types',
  'body_type',
  'height',
  'weight',
  ...FILTERABLE_GIRTH_KEYS,
  'age',
  'top_size',
  'bottom_size',
  'shoe_size',
  'language_ids',
  'country_ids',
  'city_ids',
  'skills',
  'features',
  'speech_features',
  'work_permissions',
  'filming_restrictions',
  'sport_ids',
  'dance_style_ids',
  'musical_instrument_ids',
  'vocal_style_ids',
  'extra_ids',
  'job_casting_role_ids',
  'job_film_ids',
  'job_project_types',
  'job_release_years'
] as const;

/** Названия полей фильтров портфолио, значения которых являются скалярами */
export const PORTFOLIO_SCALAR_FIELDS = [
  'gender',
  'experience',
  'job_is_short_film'
] as const;

/** Итоговый объект search-параметров портфолио */
export type IPortfolioSearchParams = Record<
  string,
  string | boolean | string[] | number[] | undefined
>;

/**
 * Проверяет, что значение фильтра непустое (не null/undefined/пустая строка).
 * Булево `false` считается заполненным значением — это осознанный выбор для полей
 * вида `job_is_short_film`, где `false` — значимый ответ, а не отсутствие фильтра.
 * @param value - значение фильтра
 */
const isFilledScalar = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

/**
 * Является ли значение диапазонного поля дефолтным («не заполнено»)
 * @param field - ключ фильтра
 * @param value - значение-массив
 */
const isDefaultRange = (field: string, value: unknown[]): boolean => {
  const defaultRange = DEFAULT_RANGES[field];

  return Boolean(defaultRange) && isDeepEqual(value, defaultRange);
};

/**
 * Собирает объект query-параметров портфолио из значений формы фильтров
 * @param filters - значения формы фильтров
 * @param extra - дополнительные параметры (status, page, per_page, username и т.п.)
 */
export const buildPortfolioSearchParams = (
  filters: Record<string, unknown> = {},
  extra: Record<string, unknown> = {}
): IPortfolioSearchParams => {
  const params: IPortfolioSearchParams = {};

  PORTFOLIO_ARRAY_FIELDS.forEach(field => {
    const value = clampRangeToBounds(filters[field], DEFAULT_RANGES[field]);
    if (!Array.isArray(value) || value.length === 0) return;
    if (isDefaultRange(field, value)) return;

    params[field] =
      field === 'job_release_years'
        ? (value as string[]).map(Number)
        : (value as string[]);
  });

  PORTFOLIO_SCALAR_FIELDS.forEach(field => {
    const value = filters[field];
    if (!isFilledScalar(value)) return;

    params[field] =
      field === 'job_is_short_film'
        ? value === true || value === 'true'
        : (value as string | boolean);
  });

  Object.entries(extra).forEach(([key, value]) => {
    if (isFilledScalar(value))
      params[key] = value as IPortfolioSearchParams[string];
  });

  return params;
};

/** DOM/form-представление job_is_short_film до приведения к типу, ожидаемому бэкендом */
type TJobIsShortFilmInput = string | boolean | null | undefined;

/**
 * Приводит job-поля payload'а поиска актёров (`users/search`) к типам, которые ждёт бэкенд:
 * `job_release_years` — массив чисел вместо DOM-строк, `job_is_short_film` — boolean вместо
 * DOM-строки 'true'/'false'. Остальные поля payload'а возвращаются без изменений — в отличие
 * от `buildPortfolioSearchParams`, актёрский поиск использует поля вне списков
 * `PORTFOLIO_ARRAY_FIELDS`/`PORTFOLIO_SCALAR_FIELDS` (например `name`), поэтому пропускать
 * весь payload через `buildPortfolioSearchParams` нельзя — часть полей будет отброшена.
 * `null` и отсутствие поля сохраняются как есть — это значимые состояния "не выбрано",
 * а литерал `false` не должен с ними совпасть.
 * @param payload - payload формы поиска актёров перед отправкой в `users/search`
 */
export const coerceActorSearchJobFields = <
  T extends {
    job_release_years?: string[];
    job_is_short_film?: TJobIsShortFilmInput;
    [key: string]: unknown;
  }
>(
  payload: T
): Omit<T, 'job_release_years' | 'job_is_short_film'> & {
  job_release_years?: number[];
  job_is_short_film?: boolean | null;
} => {
  const { job_release_years, job_is_short_film, ...rest } = payload;

  const coercedYears = Array.isArray(job_release_years)
    ? job_release_years.map(Number)
    : undefined;
  const hasShortFilmField = 'job_is_short_film' in payload;
  const coercedShortFilm =
    job_is_short_film === null
      ? null
      : job_is_short_film === true || job_is_short_film === 'true';

  return {
    ...rest,
    ...(coercedYears !== undefined ? { job_release_years: coercedYears } : {}),
    ...(hasShortFilmField ? { job_is_short_film: coercedShortFilm } : {})
  } as Omit<T, 'job_release_years' | 'job_is_short_film'> & {
    job_release_years?: number[];
    job_is_short_film?: boolean | null;
  };
};

/**
 * Возвращает значения пресета только для ключей, отсутствующих в URL (URL приоритетнее)
 * @param activeQueryParams - текущие query-параметры страницы
 * @param preset - сохранённый payload пресета
 */
export const resolvePresetForQuery = (
  activeQueryParams: Record<string, unknown>,
  preset: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  Object.entries(preset).forEach(([key, value]) => {
    const urlValue = activeQueryParams[key];
    const urlHasValue =
      urlValue !== undefined &&
      urlValue !== null &&
      !(Array.isArray(urlValue) && urlValue.length === 0);

    if (!urlHasValue) result[key] = value;
  });

  return result;
};

/**
 * Проверяет, что в объекте query-параметров страницы нет ни одного активного фильтра
 * (все значения отсутствуют/пусты). Используется, чтобы решить, можно ли на пустом URL
 * автоматически применить сохранённый пресет — если URL уже несёт хоть один фильтр
 * (deeplink, переход назад), URL побеждает и автоприменение не выполняется.
 * @param queryParams - объект query-параметров страницы (тот же, что передаётся в resolvePresetForQuery)
 */
export const isFilterQueryEmpty = (
  queryParams: Record<string, unknown>
): boolean =>
  Object.values(queryParams).every(
    value =>
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
  );

/**
 * Проверяет, есть ли в наборе значений пресета хотя бы одно активное (непустое,
 * не-дефолтное) значение. Нужно, чтобы НЕ показывать чип «восстановлены прошлые
 * фильтры» и не авто-применять пресет, состоящий только из пустых/дефолтных ключей
 * (например full-payload пресет, сохранённый мобильным клиентом, где все секции
 * присутствуют пустыми списками/null). Булево `false` (`job_is_short_film`) и
 * непустые `name`/`username` считаются активными.
 * @param values - значения пресета (обычно результат resolvePresetForQuery)
 */
export const presetHasActiveValues = (
  values: Record<string, unknown> = {}
): boolean =>
  Object.entries(values).some(([key, value]) => {
    if (value === null || value === undefined || value === '') return false;
    if (!Array.isArray(value)) return true;
    return value.length > 0 && !isDefaultRange(key, value);
  });

/**
 * Строит payload пресета портфолио как diff от дефолтов, СОХРАНЯЯ нативные типы
 * значений формы (в отличие от `buildPortfolioSearchParams`, который приводит
 * `job_release_years` к числам, а `job_is_short_film` к boolean). Это важно, чтобы
 * пресет можно было без потерь загрузить обратно в форму (`setValue`): чекбоксы годов
 * и радио метража ждут строковые значения формы. Пустой/дефолтный фильтр не попадает
 * в payload → пустой пресет не триггерит авто-применение и нет «фантомного» роста [0,250].
 * @param values - значения формы фильтров
 * @param extraKeys - дополнительные ключи (например `username`/`name`), которые нужно
 * сохранить в пресете, если они непустые
 */
export const buildActivePortfolioFormValues = (
  values: Record<string, unknown> = {},
  extraKeys: string[] = []
): Record<string, unknown> => {
  const active: Record<string, unknown> = {};

  PORTFOLIO_ARRAY_FIELDS.forEach(field => {
    const value = clampRangeToBounds(values[field], DEFAULT_RANGES[field]);
    if (!Array.isArray(value) || value.length === 0) return;
    if (isDefaultRange(field, value)) return;
    active[field] = value;
  });

  PORTFOLIO_SCALAR_FIELDS.forEach(field => {
    const value = values[field];
    if (value === null || value === undefined || value === '') return;
    active[field] = value;
  });

  extraKeys.forEach(key => {
    const value = values[key];
    if (value !== null && value !== undefined && value !== '')
      active[key] = value;
  });

  return active;
};

/**
 * Считает количество активных (непустых, не-дефолтных) фильтров портфолио —
 * для бейджа «N применённых фильтров». Опирается на `buildPortfolioSearchParams`,
 * который уже отбрасывает пустые значения и дефолтные диапазоны.
 * Поля поиска (`name`/`username`) не считаются фильтрами и в счётчик не входят.
 * @param values - значения формы фильтров
 */
export const countActivePortfolioFilters = (
  values: Record<string, unknown> = {}
): number => Object.keys(buildPortfolioSearchParams(values)).length;

/**
 * Строит объект значений формы со сброшенными полями портфолио (для кнопки
 * «Очистить всё»): массивы → `[]`, скаляры → `null`, диапазоны (рост, вес, обхваты, возраст) → дефолтные,
 * поля поиска `name`/`username` → пустая строка. Остальные (нефильтровые) поля
 * формы сохраняются как есть.
 * @param current - текущие значения формы
 */
export const buildClearedPortfolioValues = (
  current: Record<string, unknown>
): Record<string, unknown> => {
  const cleared: Record<string, unknown> = { ...current };

  PORTFOLIO_ARRAY_FIELDS.forEach(field => {
    cleared[field] = [];
  });
  Object.entries(DEFAULT_RANGES).forEach(([field, range]) => {
    cleared[field] = range;
  });

  PORTFOLIO_SCALAR_FIELDS.forEach(field => {
    cleared[field] = null;
  });

  if ('name' in cleared) cleared.name = '';
  if ('username' in cleared) cleared.username = '';

  return cleared;
};

/**
 * Строит список опций годов (по убыванию) для фильтра года релиза
 * @param fromYear - самый ранний год (по умолчанию 1950)
 */
export const buildYearOptions = (fromYear = 1950): ISelectValue[] => {
  const currentYear = new Date().getFullYear();
  const options: ISelectValue[] = [];

  for (let year = currentYear; year >= fromYear; year -= 1) {
    options.push({ id: String(year), name: String(year) });
  }

  return options;
};
